import { fetchAuthSession } from 'aws-amplify/auth';
import { STAR_GRID_API_BASE_URL } from 'src/components/star-grid/starGridConstants';
import { definitionApiError } from 'src/components/star-grid/utils/reportDefinitionError';
import { recordApiCall } from 'src/components/star-grid/services/apiCallLog';
import { BYPASS_CACHE_HEADER, isColdQueryMode } from 'src/components/star-grid/services/coldQueryMode';
import { parseServerTiming } from 'src/components/star-grid/utils/serverTiming';
import { bodyFingerprint } from 'src/components/star-grid/utils/apiCallSummary';
import { errorMessage } from 'src/components/star-grid/utils/errorMessage';

/**
 * Thrown on 401: Amplify refreshes access tokens silently, so a 401 means the refresh token itself is
 * gone and the caller has to drive a re-signin rather than show a generic error.
 */
export class AuthExpiredError extends Error {
  readonly path: string;
  constructor(path: string) {
    super(`Session expired while calling ${path} — sign in again.`);
    this.name = 'AuthExpiredError';
    this.path = path;
  }
}

/**
 * Whether a resource error is the expired-session one, so a caller showing only the message string
 * (as `useAsyncResource` does) can still raise a re-sign-in prompt instead of keeping stale data.
 */
export const isSessionExpiredMessage = (message: string | undefined): boolean => typeof message === 'string' && message.startsWith('Session expired');

async function bearerToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.accessToken?.toString();
  if (!token) {
    throw new Error('No active session — sign in to call the StarGrid API.');
  }
  return token;
}

/**
 * Recent GET answers, for the reads a page repeats one after another (the report list on every view
 * entry, each filter's hierarchy tree when the filter bar re-renders). Any write clears all of them,
 * so nothing read here can be staler than the last change this page made.
 */
const recentGets = new Map<string, { readonly value: unknown; readonly at: number }>();

const forgetRecentGets = (): void => recentGets.clear();

/** POSTs that only read (a report query, a drill, a preview): they must not clear the reused GETs. */
const READ_ONLY_POST = /\/(query|drill|preview)(\?|$)/;

/**
 * Calls the StarGrid API with the caller's FinSuite Cognito bearer token. Every call is logged for the
 * Show stats panel, and in cold query mode asks the server to bypass the StarRocks caches.
 */
export async function authedFetch(path: string, init: RequestInit): Promise<Response> {
  const token = await bearerToken();
  const cacheBypassed = isColdQueryMode();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init.headers as Record<string, string> | undefined),
    ...(cacheBypassed ? { [BYPASS_CACHE_HEADER]: 'true' } : {}),
    Authorization: `Bearer ${token}`
  };
  const method = init.method ?? 'GET';
  const bodyKey = typeof init.body === 'string' ? bodyFingerprint(init.body) : '';
  if (method !== 'GET' && !READ_ONLY_POST.test(path)) forgetRecentGets();
  const startedAt = Date.now();
  const started = performance.now();
  let response: Response;
  try {
    response = await fetch(`${STAR_GRID_API_BASE_URL}${path}`, { ...init, cache: 'no-store', headers });
  } catch (error) {
    // Unmounts, navigation and shared-GET cancellation abort routinely; only real failures are logged.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    const message = errorMessage(error);
    recordApiCall({ method, path, bodyKey, status: null, startedAt, durationMs: performance.now() - started, cacheBypassed, error: message });
    throw error;
  }
  // Test doubles and some proxies answer without headers; the log just goes without server times.
  const timing = parseServerTiming(response.headers?.get('Server-Timing') ?? null);
  recordApiCall({
    method,
    path,
    bodyKey,
    status: response.status,
    startedAt,
    durationMs: performance.now() - started,
    serverMs: timing.appMs,
    starRocksMs: timing.starRocksMs,
    starRocksQueries: timing.starRocksQueries,
    cacheBypassed
  });
  if (response.status === 401) {
    throw new AuthExpiredError(path);
  }
  return response;
}

function validationMessages(detail: readonly unknown[]): string {
  return detail
    .map((item: unknown) => (item && typeof item === 'object' && 'msg' in item && typeof item.msg === 'string' ? item.msg : ''))
    .filter(Boolean)
    .join('; ');
}

/** The FastAPI `detail` message of an error response, falling back to the status line. */
export async function apiErrorDetail(response: Response, path: string): Promise<string> {
  try {
    const body: { detail?: unknown } = await response.json();
    const detail = body?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'error' in detail && typeof detail.error === 'string') return detail.error;
    if (Array.isArray(detail)) return validationMessages(detail) || `Invalid request (${response.status})`;
  } catch {
    // Non-JSON body — fall through to the status line.
  }
  return `StarGrid API ${path} failed: ${response.status} ${response.statusText}`;
}

interface InFlightGet {
  readonly promise: Promise<unknown>;
  readonly controller: AbortController;
  waiters: number;
}

/** GETs in flight, by key, shared by components mounting together. */
const inFlightGets = new Map<string, InFlightGet>();

const abortError = (): DOMException => new DOMException('The operation was aborted.', 'AbortError');

/** One GET per `key` at a time; an abort rejects only its caller, and the request stops once no caller is left. */
const sharedGet = <T>(key: string, path: string, read: (response: Response) => Promise<unknown>, signal?: AbortSignal): Promise<T> => {
  if (signal?.aborted) return Promise.reject(abortError());
  let entry = inFlightGets.get(key);
  if (!entry) {
    const controller = new AbortController();
    const promise = authedFetch(path, { method: 'GET', signal: controller.signal })
      .then(read)
      .finally(() => {
        if (inFlightGets.get(key)?.controller === controller) inFlightGets.delete(key);
      });
    entry = { promise, controller, waiters: 0 };
    inFlightGets.set(key, entry);
  }
  const shared = entry;
  shared.waiters += 1;
  return new Promise<T>((resolve, reject) => {
    const leave = (): void => {
      shared.waiters -= 1;
      signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = (): void => {
      leave();
      reject(abortError());
      // A tick later, so React's dev remount (abort, then the same request again) rejoins this one.
      setTimeout(() => {
        if (shared.waiters > 0) return;
        if (inFlightGets.get(key) === shared) inFlightGets.delete(key);
        shared.controller.abort();
      }, 0);
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    shared.promise.then(
      (value) => {
        if (signal?.aborted) return;
        leave();
        resolve(value as T);
      },
      (error: unknown) => {
        if (signal?.aborted) return;
        leave();
        reject(error);
      }
    );
  });
};

/** Seeds what a GET of `path` answers, from a response that already carried it. */
export const rememberGet = (path: string, value: unknown): void => {
  recentGets.set(path, { value, at: Date.now() });
};

/** `maxAgeMs` reuses an answer that young instead of asking again; omit it to always ask. */
const recentOr = async <T>(path: string, maxAgeMs: number | undefined, read: () => Promise<T>): Promise<T> => {
  const recent = maxAgeMs ? recentGets.get(path) : undefined;
  if (recent && Date.now() - recent.at < (maxAgeMs ?? 0)) return recent.value as T;
  const value = await read();
  if (maxAgeMs) rememberGet(path, value);
  return value;
};

export const getJson = <T>(path: string, signal?: AbortSignal, maxAgeMs?: number): Promise<T> =>
  recentOr(path, maxAgeMs, () =>
    sharedGet<T>(
      `json ${path}`,
      path,
      async (response) => {
        if (!response.ok) throw new Error(await apiErrorDetail(response, path));
        return (await response.json()) as unknown;
      },
      signal
    )
  );

/** A GET whose failure throws `ReportDefinitionApiError`, keeping the status and field-level reasons. */
export const getDefinition = (path: string, signal?: AbortSignal, maxAgeMs?: number): Promise<unknown> =>
  recentOr(path, maxAgeMs, () =>
    sharedGet<unknown>(
      `definition ${path}`,
      path,
      async (response) => {
        const value: unknown = await response.json().catch(() => undefined);
        if (!response.ok) throw definitionApiError(response.status, value);
        return value;
      },
      signal
    )
  );

export async function sendJson<T>(method: 'POST' | 'PUT' | 'PATCH', path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const response = await authedFetch(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal
  });
  if (!response.ok) throw new Error(await apiErrorDetail(response, path));
  return (await response.json()) as T;
}

export async function deleteResource(path: string): Promise<void> {
  const response = await authedFetch(path, { method: 'DELETE' });
  if (!response.ok) throw new Error(await apiErrorDetail(response, path));
}

/**
 * GET without a body, POST (or `method`) with one. Failures throw `ReportDefinitionApiError`, which keeps
 * the status and the field-level reasons callers branch on.
 */
export const apiRequest = async (path: string, body?: unknown, signal?: AbortSignal, method?: 'POST' | 'PUT'): Promise<unknown> => {
  if (body === undefined && method === undefined) return getDefinition(path, signal);
  const response = await authedFetch(path, {
    method: body === undefined ? 'GET' : method ?? 'POST',
    signal,
    ...(body === undefined ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
  });
  const value: unknown = await response.json().catch(() => undefined);
  if (!response.ok) throw definitionApiError(response.status, value);
  return value;
};
